import { styled } from '@ori-ui/mui';
import { GlobalApplicationsPersonalMenuPersonalMenuDtoUserType } from '@ori/global-api-client-react';
import type { FC } from 'react';

import { TranslationKey } from '../models/TranslationKey';
import type { User } from '../schemas/userSchema';
import { useConsultantNameFormatters } from '../utils/useConsultantNameFormatters';
import { FormattedMessage } from './FormattedMessage';

export interface PersonalizationProps {
  user: User;
  upgradeToConsultantUrl: string;
  upgradeToBrandPartnerUrl: string;
}

const Container = styled('div')(({ theme }) => ({
  display: 'grid',
  boxSizing: 'border-box',
  gridTemplateColumns: '1fr',
  gridTemplateRows: `auto minmax(${theme.spacing(2)}, auto)`,
  width: '100%',
  borderBottom: `2px solid ${theme.palette.grey[100]}`,

  '& link': {
    display: 'block',
    padding: theme.spacing(2, 3, 2, 3),
  },
}));

const Info = styled('div')(({ theme }) => ({
  alignSelf: 'center',
  padding: theme.spacing(2, 3, 2, 3),
  color: theme.palette.grey[900],
  fontSize: '12px',
  fontWeight: 'bold',
}));

const Name = styled('span')(({ theme }) => ({
  display: 'block',
  overflow: 'hidden',
  fontSize: '18px',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const Action = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 3, 3),
}));
const Button = styled('a')(({ theme }) => ({
  display: 'block',
  boxSizing: 'border-box',
  width: '100%',
  height: theme.spacing(6),
  paddingTop: theme.spacing(1),
  borderRadius: theme.spacing(1),
  background: theme.palette.grey[100],
  color: theme.palette.grey[700],
  fontSize: '14px',
  fontWeight: 'bold',
  textAlign: 'center',
}));

export const Personalisation: FC<PersonalizationProps> = ({
  user,
  upgradeToConsultantUrl,
  upgradeToBrandPartnerUrl,
}) => {
  const { getFormattedConsultantName } = useConsultantNameFormatters();

  if (user.userType === GlobalApplicationsPersonalMenuPersonalMenuDtoUserType.Employee) {
    return null;
  }

  let upgradeToConsultant = null;

  if (user.userType === GlobalApplicationsPersonalMenuPersonalMenuDtoUserType.EndCustomer && upgradeToConsultantUrl) {
    upgradeToConsultant = (
      <Action>
        <Button href={upgradeToConsultantUrl}>
          <FormattedMessage id={TranslationKey.UpgradeToConsultant} />
        </Button>
      </Action>
    );
  }

  let upgradeToBrandPartner = null;
  if (user.userType === GlobalApplicationsPersonalMenuPersonalMenuDtoUserType.EndCustomer && upgradeToBrandPartnerUrl) {
    upgradeToBrandPartner = (
      <Action>
        <Button href={upgradeToBrandPartnerUrl}>
          <FormattedMessage id={TranslationKey.UpgradeToBrandPartner} />
        </Button>
      </Action>
    );
  }

  return (
    <Container>
      <Info>
        <FormattedMessage id={TranslationKey.Hello} />,
        <br />
        <Name>{getFormattedConsultantName(user.firstName, user.lastName)}</Name>
      </Info>
      {upgradeToConsultant}
      {upgradeToBrandPartner}
    </Container>
  );
};
